'use client';

import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/shared/Button';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import type { AmbassadorContent } from '@/lib/content-schema';

interface Props {
  content: AmbassadorContent['form'];
}

export function ApplicationForm({ content }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const posthog = usePostHog();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    nombre: '',
    email: '',
    canal: '',
    seguidores: '',
    zona: '',
    eventos: '',
    motivacion: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    posthog?.capture('ambassador_form_submitted', { page: 'ambassadors' });
    setSubmitted(true);
  };

  const inputClass =
    'w-full bg-white border border-zibarit-marron-secundario/30 rounded-xl px-4 py-3 text-body text-sm focus:border-zibarit-verde-oscuro focus:outline-none transition-colors';

  return (
    <section id="aplicar" className="py-24 md:py-32 bg-zibarit-bone">
      <motion.div
        ref={ref}
        variants={staggerContainer}
        initial="initial"
        animate={isInView ? 'animate' : 'initial'}
        className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8"
      >
        <motion.div variants={fadeInUp} className="text-center mb-12">
          <h2 className="text-title text-3xl md:text-4xl text-zibarit-negro mb-4">
            {content.title}
          </h2>
          <p className="text-body text-base text-zibarit-marron-oscuro">
            {content.subtitle}
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            variants={fadeInUp}
            className="bg-white rounded-2xl p-8 md:p-12 text-center shadow-sm"
          >
            <div className="w-16 h-16 rounded-full bg-zibarit-verde-oscuro/10 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-zibarit-verde-oscuro" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-title text-2xl text-zibarit-negro mb-3">
              {content.successTitle}
            </h3>
            <p className="text-body text-sm text-zibarit-marron-oscuro">
              {content.successMessage}
            </p>
          </motion.div>
        ) : (
          <motion.form
            variants={fadeInUp}
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-6 md:p-10 shadow-sm space-y-5"
          >
            <div>
              <label htmlFor="nombre" className="text-subtitle text-xs uppercase tracking-wider text-zibarit-negro mb-1.5 block">Nombre</label>
              <input id="nombre" name="nombre" type="text" required value={form.nombre} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="email" className="text-subtitle text-xs uppercase tracking-wider text-zibarit-negro mb-1.5 block">Email</label>
              <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="canal" className="text-subtitle text-xs uppercase tracking-wider text-zibarit-negro mb-1.5 block">Canal principal</label>
              <input id="canal" name="canal" type="text" required placeholder="@tu_handle en Instagram, TikTok..." value={form.canal} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="seguidores" className="text-subtitle text-xs uppercase tracking-wider text-zibarit-negro mb-1.5 block">Seguidores</label>
              <select id="seguidores" name="seguidores" required value={form.seguidores} onChange={handleChange} className={inputClass}>
                <option value="">Selecciona un rango</option>
                <option value="1K-10K">1K - 10K</option>
                <option value="10K-50K">10K - 50K</option>
                <option value="50K-100K">50K - 100K</option>
                <option value="100K+">100K+</option>
              </select>
            </div>
            <div>
              <label htmlFor="zona" className="text-subtitle text-xs uppercase tracking-wider text-zibarit-negro mb-1.5 block">Zona de Madrid</label>
              <input id="zona" name="zona" type="text" required placeholder="Barrio o zona donde vives" value={form.zona} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="eventos" className="text-subtitle text-xs uppercase tracking-wider text-zibarit-negro mb-1.5 block">Tipo de eventos que te interesan</label>
              <textarea id="eventos" name="eventos" rows={3} required placeholder="Catas de vino, yoga-brunches, cenas pop-up..." value={form.eventos} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label htmlFor="motivacion" className="text-subtitle text-xs uppercase tracking-wider text-zibarit-negro mb-1.5 block">¿Por qué quieres ser embajador/a?</label>
              <textarea id="motivacion" name="motivacion" rows={3} required value={form.motivacion} onChange={handleChange} className={inputClass} />
            </div>
            <Button type="submit" className="w-full bg-zibarit-verde-oscuro text-zibarit-bone hover:bg-zibarit-gris-oscuro" size="lg" data-ph-capture>
              {content.submitText}
            </Button>
            <p className="text-body text-xs text-zibarit-marron-oscuro/60 text-center">
              {content.footerNote}
            </p>
          </motion.form>
        )}
      </motion.div>
    </section>
  );
}
